import {
  compareText,
  isProjectOpen,
  type ProjectOption,
  routes,
  type Task,
  type TaskInput,
  taskInputSchema,
} from '@pmai/contracts';
import { CheckboxField, FormDialog, type SelectOption, SelectField, TextField } from '@pmai/ui';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../shared/api/hooks';
import { bind, bindSelect } from '../../shared/forms/form';
import { useEntityForm } from '../../shared/forms/use-entity-form';

/** Where a task form is opened: the project is fixed, one customer's projects, or any project. */
export type TaskScope = { kind: 'project'; id: string } | { kind: 'customer'; id: string } | { kind: 'all' };

/** Every change to tasks refreshes the task lists and "projects without tasks" (FR-TASK-019). */
export const TASK_INVALIDATES = [routes.tasks.list, routes.projects.withoutTasks];

const emptyTask = (projectId = ''): TaskInput => ({
  projectId,
  title: '',
  assigneeId: '',
  estimatedHours: '',
  actualHours: '',
  deadline: '',
  isPriority: false,
  done: false,
});

const valuesOf = (task: Task): TaskInput => ({
  projectId: task.projectId,
  title: task.title,
  assigneeId: task.assigneeId ?? '',
  estimatedHours: task.estimatedHours === null ? '' : String(task.estimatedHours),
  actualHours: task.actualHours === null ? '' : String(task.actualHours),
  deadline: task.deadline ?? '',
  isPriority: task.isPriority,
  done: task.done,
});

/** Open projects A→Z, then the groups "Pabeigti" and "Atcelti" (FR-TASK-016). */
function useProjectOptions(projects: readonly ProjectOption[] | undefined): SelectOption[] {
  const { t } = useTranslation();
  const rank = (p: ProjectOption) => (isProjectOpen(p.status) ? 0 : p.status === 'completed' ? 1 : 2);
  return [...(projects ?? [])]
    .sort((a, b) => rank(a) - rank(b) || compareText(a.name, b.name))
    .map((p) => ({
      value: p.id,
      label: p.name,
      group: isProjectOpen(p.status) ? undefined : t(`tasks.closedGroup.${p.status}`),
    }));
}

/**
 * The one task form (FR-TASK-001/006/016/017, FR-CUST-015): project (unless fixed), what to do,
 * responsible resource, deadline, hours, priority and done.
 */
export function TaskDialog({
  open,
  task,
  scope,
  onClose,
}: {
  open: boolean;
  task: Task | null;
  scope: TaskScope;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const choosesProject = scope.kind !== 'project';
  const resources = useApiQuery(routes.resources.list, {}, { enabled: open });
  const projects = useApiQuery(routes.projects.options, {}, { enabled: open && choosesProject });
  const projectOptions = useProjectOptions(
    scope.kind === 'customer' ? projects.data?.filter((p) => p.customerId === scope.id) : projects.data,
  );
  const fixedProject = scope.kind === 'project' ? scope.id : '';
  const { form, create, update, dialog } = useEntityForm({
    schema: taskInputSchema,
    open,
    record: task,
    values: task ? valuesOf(task) : emptyTask(fixedProject),
    create: routes.tasks.create,
    update: routes.tasks.update,
    invalidates: TASK_INVALIDATES,
    onClose,
  });

  const f = (field: string) => t(`tasks.fields.${field}`);
  return (
    <FormDialog
      {...dialog}
      title={task ? t('tasks.editTitle') : t('tasks.newTitle')}
      submitLabel={task ? t('common.update') : undefined}
      onSubmit={form.handleSubmit((body) =>
        task
          ? update.mutate({ params: { id: task.id }, body: { ...body, version: task.version } })
          : create.mutate({ body }),
      )}
    >
      {choosesProject && (
        <SelectField
          label={f('project')}
          required
          placeholder={t('common.notSpecified')}
          options={projectOptions}
          {...bindSelect(form, 'projectId')}
        />
      )}
      <TextField label={f('title')} required {...bind(form, 'title')} />
      <SelectField
        label={f('assignee')}
        placeholder={t('common.notSpecified')}
        options={(resources.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
        {...bindSelect(form, 'assigneeId')}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField label={f('deadline')} type="date" {...bind(form, 'deadline')} />
        <TextField label={f('estimatedHours')} inputMode="decimal" {...bind(form, 'estimatedHours')} />
        <TextField label={f('actualHours')} inputMode="decimal" {...bind(form, 'actualHours')} />
      </div>
      <div className="flex flex-wrap gap-6">
        <CheckboxField label={f('isPriority')} {...form.register('isPriority')} />
        <CheckboxField label={f('done')} {...form.register('done')} />
      </div>
    </FormDialog>
  );
}
